
    // Vue, third party
    import { Component, Watch, Vue, Mixins, Prop } from "vue-property-decorator";
    import { State, Getter, Action, Mutation, namespace } from "vuex-class";

    // Sv Components
    import SureViewIcon from "@/components/SureViewIcon.vue"

    // Third Party Components
    import VuePerfectScrollbar from "vue-perfect-scrollbar";
    import vselect3 from "vselect3";

    // Types
    import { ActionPlan, TaskCategory, TaskCategoryTypeId, TaskLibrary, TaskType, NewTask } from '@/store/tasks/types';
    import { VueBootstrapField } from '@/store/types';
	import { TaskCategoryType } from "@/models/Tasks/TaskCategoryType";

    // Namespaces
    const Tasks = namespace("tasks");

    @Component({
        components: {
            vselect3,
            "sureview-icon": SureViewIcon,
            "vue-perfect-scrollbar": VuePerfectScrollbar,
        }
    })
    export default class ActionCategoryModal extends Vue {
        @Tasks.Action updateTaskCategory: (payload: TaskCategory) => Promise<void>;
        @Tasks.Action createTaskCategory: (payload: TaskCategory) => Promise<void>;
        @Tasks.Action deleteTaskCategory: (categoryId: number) => Promise<void>;

        @Prop({ default: () => ([]), type: Array }) actionCategories: TaskCategory[];
        @Prop({ default: false, type: Boolean }) categoriesModal: boolean;

        @Prop({ default: false, type: Boolean }) deleteCategoryModal: boolean;
        @Prop({ default: false, type: Boolean }) categoryModal: boolean;

        private fields: VueBootstrapField[] = [
            { key: 'title', label: 'Title', sortable: true },
            { key: 'taskCategoryTypeID', label: 'Type', sortable: false },
            { key: 'actions', label: 'Actions', sortable: false }
        ];

        private currentCategory: TaskCategory = null;
        private editing: boolean = false;
        private currentCategoryTitle: string = "";
        private search: string = "";
        private isBusy: boolean = true;
		private taskCategoryTypeID: TaskCategoryTypeId = TaskCategoryTypeId.Alarm;

        private mounted() {
            if (this.actionCategories) {
                this.isBusy = false;
            }
        }

        /**
         * @summary emit event to toggle modal visibility status
         * @returns state of visibility
         */
        private emitCloseCategoryModal(state: boolean) {
            this.$emit("category-modal-state", state);
        }

        @Watch("actionCategories")
        private setBusyState(newVal: TaskCategory[] | null) {
            if (!newVal) return;
            this.isBusy = false;
        }


        /**
         * @summary watcher that emits the current category.
         * @emit current category
         * @returns the current category
         */
        @Watch("currentCategory", { deep: true })
        private emitCurrentCategory(): void {
            this.$emit("current-category", this.currentCategory);
        }

        /**
         * @summary method to set the category to edit
         * @returns void
         */
        private setCurrentCategoryToEdit(category: TaskCategory) {
            this.editing = true;
            this.currentCategory = category;
            this.currentCategoryTitle = category.title;
            this.categoryModal = true;
            this.taskCategoryTypeID = category.taskCategoryTypeID;
        }

        /**
         * @summary set current category to delete
         * @returns void
         */
        private setCurrentCategoryToDelete(category: TaskCategory) {
            this.deleteCategoryModal = true;
            this.currentCategory = category;
        }

        /**
         * @summary method to save or update current category.
         * @returns void
         */
        private async saveCurrentCategory() {
            this.editing ? await this.updateCurrentCategory() : await this.saveNewCategory();
        }

        /**
         * @summary method to save new category
         * @returns Promise<void>
         */
        private async saveNewCategory(): Promise<void> {
            const newCategory: TaskCategory = {
                title: this.currentCategoryTitle,
                taskCategoryID: null,
                tasks: null,
                selected: false,
                groupID: null,
                taskCategoryTypeID: this.taskCategoryTypeID,
            };

            await this.createTaskCategory(newCategory);
            this.currentCategoryTitle = null;
            this.categoryModal = false;
        }

        /**
         * @summary method to update current category#
         * @returns Promise<void>
         */
        private async updateCurrentCategory(): Promise<void> {
            this.editing = false;

            this.currentCategory.taskCategoryTypeID = this.taskCategoryTypeID;
            this.currentCategory.title = this.currentCategoryTitle;

            await this.updateTaskCategory(this.currentCategory);

            this.currentCategory = null;
            this.currentCategoryTitle = null;
            this.categoryModal = false;
        }

        /**
         * @summary method to delete selected category
         * @returns Promise<void>
         */
        private async deleteSelectedCategory(): Promise<void> {
            await this.deleteTaskCategory(this.currentCategory.taskCategoryID);
            this.deleteCategoryModal = false;
        }

        /**
         * @summary method to reset category values for new category
         * @returns void
         */
        private resetNewCategory() {
            this.currentCategory = null;
            this.currentCategoryTitle = "";
            this.editing = false;
            this.categoryModal = true;
        }

        private get taskCategoryTypeOptions() : any[] {
            let map = [];

            for(const categoryType in TaskCategoryTypeId) {
                if (typeof TaskCategoryTypeId[categoryType] === 'number') {
                    map.push({label: categoryType, id: parseInt(TaskCategoryTypeId[categoryType]) });
                }
            }

            return map;
        }

        private getTaskCategoryTypeName(typeId?: number) {
            if (!typeId)
            {
                return TaskCategoryTypeId[TaskCategoryTypeId.Alarm];
            }
            return TaskCategoryTypeId[typeId];
        }
    }
